// Format de `video_moments.annotations` (colonne JSONB).
//
//   [ { t: 12.4, elements: [ ...6 types... ] },
//     { t: 31.8, elements: [ ... ] } ]
//
// Une entree = un point d'arret sur une frame precise. `t` est en secondes
// depuis le debut de la video. `elements` est le tableau des annotations SVG
// (halo, arrow, line, zone, text, tactical_line) en coordonnees normalisees
// 0-1, structure inchangee par rapport a l'editeur.
//
// Il n'y a volontairement pas de champ duree : une annotation est collee a une
// image, elle n'a pas de plage de temps. Le tableau est trie par t croissant.
//
// Format herite (mai 2026) : l'ancien editeur ecrivait un tableau plat de
// formes — { type, color, x1, y1, x2, y2 } ou { type, color, cx, cy, r } — sans
// aucune notion de t. Ces entrees sont ignorees en lecture (isAnnotationSet les
// rejette) et disparaissent d'elles-memes a la premiere sauvegarde du moment,
// qui reecrit le tableau entier au format cible. Pas de convertisseur : 3
// moments de test concernes, zero client en production.

// Deux jeux distants de moins d'une seconde visent la meme frame : on remplace
// au lieu de dupliquer.
//
// La valeur ne couvre pas l'imprecision machine — celle-ci se compte en
// microsecondes — mais le geste humain : un coach qui revient sur sa frame en
// deplacant la tete de lecture a la main ne retombe jamais a 300 ms pres. Le
// seuil precedent (0,3 s) ne repondait qu'au cas du seek programme, et rendait
// une annotation introuvable des que le retour etait manuel.
export const SET_MATCH_TOLERANCE = 1.0

export function isAnnotationSet(entry) {
  return !!entry
    && typeof entry === 'object'
    && !Array.isArray(entry)
    && Number.isFinite(entry.t)
    && Array.isArray(entry.elements)
}

// Un seul avertissement par session, quel que soit le nombre de moments
// herites ouverts : le coach n'a rien a faire de cette information.
let legacyWarned = false

/**
 * Normalise la valeur brute de moment.annotations en jeux valides, tries par t.
 * Les entrees au format herite sont ecartees silencieusement cote UI.
 */
export function parseAnnotationSets(raw) {
  if (!Array.isArray(raw)) return []
  const sets = raw.filter(isAnnotationSet)
  if (sets.length !== raw.length && !legacyWarned) {
    legacyWarned = true
    console.warn(
      '[annotations] Entrees au format herite (mai 2026) ignorees : elles seront ' +
      'remplacees par le format { t, elements } a la prochaine sauvegarde du moment.',
    )
  }
  return sets.sort((a, b) => a.t - b.t)
}

// Le plus proche de `t` dans la tolerance, et non le premier rencontre. La
// distinction est devenue reelle en passant a 1 s : deux jeux peuvent tomber
// dans la fenetre en meme temps (la prod en porte deux a 0,62 s d'ecart), et
// prendre le premier ouvrirait une frame que le coach n'a pas visee.
function nearestByT(entries, t) {
  if (!Number.isFinite(t)) return null
  let best = null
  let bestDist = Infinity
  for (const e of entries || []) {
    const dist = Math.abs(e.t - t)
    if (dist <= SET_MATCH_TOLERANCE && dist < bestDist) {
      best = e
      bestDist = dist
    }
  }
  return best
}

/** Le jeu qui vise la frame `t`, ou null. */
export function findAnnotationSetAt(sets, t) {
  return nearestByT(sets, t)
}

/**
 * Le point d'arret annote qui vise la frame `t`, ou null. Meme question que
 * findAnnotationSetAt, posee sur tous les moments du match au lieu d'un seul :
 * c'est ce qui permet au CTA "Annoter" de rouvrir un dessin existant au lieu
 * de creer un moment de plus sur la meme image.
 */
export function findAnnotationPointAt(points, t) {
  return nearestByT(points, t)
}

/**
 * Insere ou remplace le jeu de la frame `t` et retourne le tableau complet,
 * trie, pret a etre persiste. Un `elements` vide supprime le jeu : le coach a
 * tout efface puis enregistre.
 */
/**
 * Aplatit tous les jeux de tous les moments d'un match en une liste unique de
 * points d'arret, triee par timecode croissant.
 *
 *   [{ t, elements, momentId, momentType }, ...]
 *
 * Un moment peut porter plusieurs jeux : ils deviennent autant de points
 * distincts. Alimente le compteur du bouton de lecture, les marqueurs de
 * timeline et le mode lecture — une seule source, pas trois calculs paralleles
 * qui pourraient diverger.
 */
export function collectAnnotationPoints(moments) {
  const points = []
  for (const m of moments || []) {
    for (const set of parseAnnotationSets(m?.annotations)) {
      if (!set.elements.length) continue
      points.push({
        t: set.t,
        elements: set.elements,
        momentId: m.id,
        momentType: m.moment_type,
      })
    }
  }
  return points.sort((a, b) => a.t - b.t)
}

/** Nombre de jeux affichables portes par un moment (libelle du crayon). */
export function countAnnotationSets(moment) {
  return parseAnnotationSets(moment?.annotations).filter(s => s.elements.length > 0).length
}

export function upsertAnnotationSet(raw, t, elements) {
  const sets = parseAnnotationSets(raw)
  // On remplace EXACTEMENT le jeu que findAnnotationSetAt aurait charge, et
  // lui seul. L'ancienne version ecartait tous les jeux de la fenetre : a 0,3 s
  // c'etait sans consequence, a 1 s un enregistrement effacerait le voisin que
  // le coach n'a jamais ouvert.
  const target = findAnnotationSetAt(sets, t)
  const kept = sets.filter(s => s !== target)
  if (Array.isArray(elements) && elements.length > 0) {
    // Le `t` du jeu rouvert fait foi : c'est l'image sur laquelle le dessin a
    // ete pose. Le `t` recu n'est qu'une position de tete de lecture, a une
    // seconde pres — le reprendre ferait deriver l'annotation frame apres
    // frame, a chaque correction.
    kept.push({ t: target ? target.t : t, elements })
  }
  return kept.sort((a, b) => a.t - b.t)
}
